import { useState, useRef, useEffect } from "react";
import { MessageSquare, Send, X, Loader2, Sparkles, User, Bot, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import GlassCard from "@/components/GlassCard";
import { useAppliances } from "@/hooks/useAppliances";
import { usePaintCodes } from "@/hooks/usePaintCodes";
import { useQuickInfo } from "@/hooks/useQuickInfo";
import { useFurniture } from "@/hooks/useFurniture";
import { usePlants } from "@/hooks/usePlants";
import { useServiceLogs } from "@/hooks/useServiceLogs";
import { useProviders } from "@/hooks/useProviders";
import { useHomeLibrary } from "@/hooks/useHomeLibrary";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";

const AI_SUGGESTIONS = [
    "Lounge paint color?",
    "Next service due?",
    "Who is my plumber?",
    "Water shut-off location?",
    "WiFi password?",
    "Plants needing water?",
];

// Fix: keywords like appliance names, brands, or paint codes can contain
// regex-special characters (e.g. "3.5kW", "A/C (Main)"), which previously
// went straight into `new RegExp(...)` unescaped and could throw a
// SyntaxError, crashing the whole assistant on a perfectly normal question.
const escapeRegExp = (string) => String(string).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const AskHomeEzi = ({ mode = "floating", className, onInputFocus }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: "assistant", content: "Hi! I'm your home assistant. Ask me anything about your property." }
    ]);
    const [input, setInput] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
    const [keyboardHeight, setKeyboardHeight] = useState(0);
    const messagesContainerRef = useRef(null);
    const messagesEndRef = useRef(null); // Keep for legacy refs if needed, but primary scroll is container

    const scrollToBottom = () => {
        if (messagesContainerRef.current) {
            const { scrollHeight, clientHeight } = messagesContainerRef.current;
            messagesContainerRef.current.scrollTop = scrollHeight - clientHeight;
        }
    };

    useEffect(() => {
        // Only auto-scroll if it's open (floating) or always (embedded)
        // But preventing 'window' scroll is handled by using scrollTop on the div above.
        if (isOpen || mode === "embedded") {
            setTimeout(scrollToBottom, 100);
        }
    }, [messages, isTyping, isOpen, mode]);

    useEffect(() => {
        if (typeof window === "undefined" || !Capacitor.isPluginAvailable("Keyboard")) return;

        const showListener = Keyboard.addListener("keyboardWillShow", () => {
            setIsKeyboardOpen(true);
        });
        const hideListener = Keyboard.addListener("keyboardWillHide", () => {
            setIsKeyboardOpen(false);
            setKeyboardHeight(0);
        });

        return () => {
            showListener.remove();
            hideListener.remove();
        };
    }, []);

    // Data hooks to search locally
    const { activeProperty } = usePropertyContext();
    const { appliances } = useAppliances(activeProperty?.id);
    const { paintCodes } = usePaintCodes(activeProperty?.id);
    const { quickInfo } = useQuickInfo(activeProperty?.id);
    const { furniture } = useFurniture(activeProperty?.id);
    const { plants } = usePlants(activeProperty?.id);
    const { serviceLogs } = useServiceLogs(activeProperty?.id);
    const { providers } = useProviders(); // Fetches all providers available to the user
    const { libraryItems: vaultItems } = useHomeLibrary(activeProperty?.id);

    // This is a "Zero Cost" search engine.
    // Instead of calling an expensive LLM API, we use regex/keyword matching on the client-side data.
    // It's free, instant, and private.
    const searchHome = (query) => {
        const q = query.toLowerCase();

        // Helper to check if query contains any of the keywords
        // Helper to check if query contains any of the keywords as whole words (handles plurals)
        const contains = (keywords) => keywords.some(k => {
            if (!k) return false;
            const regex = new RegExp(`\\b${escapeRegExp(k)}(s|es)?\\b`, 'i');
            return regex.test(q);
        });

        // Helper to check for whole word or plural (e.g., 'plan' matches 'plans')
        const matchesWordOrPlural = (keyword, text) => {
            if (!keyword) return false;
            const regex = new RegExp(`\\b${escapeRegExp(keyword)}(s|es)?\\b`, 'i');
            return regex.test(text);
        };

        // Fix (bucket priority): if the query names a specific appliance
        // (e.g. "when is my fridge next due"), that should be answered by the
        // per-appliance lookup below (bucket 5), not swallowed by the generic
        // "due/agenda" bucket (1.5) just because it also contains "due"/"next".
        const namedAppliance = appliances.find(a => contains([a.name, a.brand, a.model_number]));

        // 1. GREETINGS & HELP
        if (contains(["hi", "hello", "hey", "help", "what can you do", "capabilities"])) {
            return "I can help you find information about your home! Try asking:\n" +
                "• Paint: 'What color is the lounge?'\n" +
                "• Appliances: 'When did I buy the fridge?'\n" +
                "• Maintenance: 'When is my next service due?'\n" +
                "• Contacts: 'Who is my plumber?'\n" +
                "• Records: 'Where is my title deed?'\n" +
                "• Plants: 'Which plants need water?'\n" +
                "• Tasks & Due Items: 'Is there anything due tomorrow?'";
        }

        // 1.5. UPCOMING / DUE AGENDA
        // Skipped when a specific appliance is named — bucket 5 handles that
        // more precisely (see namedAppliance above).
        if (!namedAppliance && contains(["due", "tomorrow", "upcoming", "soon", "schedule", "watering", "service", "task", "sub-task", "subtask", "agenda", "calendar"])) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            const thirtyDaysFromNow = new Date(today);
            thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
            thirtyDaysFromNow.setHours(23, 59, 59, 999);

            const isDueTomorrow = q.includes("tomorrow");
            // Only "today" and no wider-window word narrows to today-only;
            // otherwise ("due soon", "upcoming", "what's due", plain "service
            // due", etc.) default to the same 30-day window the Dashboard's
            // getUpcomingServiceAppliances()/getUpcomingSubTasks() use, so the
            // assistant's answer matches what's shown as a Recommended Action.
            const isDueTodayOnly = !isDueTomorrow && q.includes("today") && !contains(["soon", "upcoming", "week", "month"]);

            let windowEnd;
            let targetDayStr;
            if (isDueTomorrow) {
                windowEnd = tomorrow;
                targetDayStr = "by tomorrow";
            } else if (isDueTodayOnly) {
                windowEnd = today;
                targetDayStr = "today";
            } else {
                windowEnd = thirtyDaysFromNow;
                targetDayStr = "in the next 30 days";
            }

            const dayLabel = (date) => {
                const diffDays = Math.round((date - today) / (1000 * 60 * 60 * 24));
                if (diffDays < 0) return "OVERDUE";
                if (diffDays === 0) return "Today";
                if (diffDays === 1) return "Tomorrow";
                return `in ${diffDays} days`;
            };

            // Check Plants
            const duePlants = plants.filter(p => {
                if (p.is_paused || !p.next_watering_at) return false;
                const d = new Date(p.next_watering_at);
                d.setHours(0, 0, 0, 0);
                return d <= windowEnd;
            });

            // Check Appliance Services
            const dueAppliances = appliances.filter(a => {
                if (!a.next_service_date) return false;
                const d = new Date(a.next_service_date);
                d.setHours(0, 0, 0, 0);
                return d <= windowEnd;
            });

            // Check Sub-tasks (Appliance and Furniture checklists)
            const dueSubtasks = [];
            appliances.forEach(a => {
                (a.maintenance_tasks || []).forEach(t => {
                    if (!t.next_service_date) return;
                    const d = new Date(t.next_service_date);
                    d.setHours(0, 0, 0, 0);
                    if (d <= windowEnd) {
                        dueSubtasks.push({ item: a.name, task: t.name, date: t.next_service_date, type: "Appliance" });
                    }
                });
            });
            furniture.forEach(f => {
                (f.maintenance_tasks || []).forEach(t => {
                    if (!t.next_service_date) return;
                    const d = new Date(t.next_service_date);
                    d.setHours(0, 0, 0, 0);
                    if (d <= windowEnd) {
                        dueSubtasks.push({ item: f.name, task: t.name, date: t.next_service_date, type: "Furniture" });
                    }
                });
            });

            const hasWatering = duePlants.length > 0;
            const hasAppliances = dueAppliances.length > 0;
            const hasSubtasks = dueSubtasks.length > 0;

            if (!hasWatering && !hasAppliances && !hasSubtasks) {
                return `You have no tasks, watering schedules, or services due ${targetDayStr}! All systems are clear.`;
            }

            let response = `Here is your property agenda due ${targetDayStr}:\n`;

            if (hasWatering) {
                response += `\n🪴 **Plant Watering Checklist:**\n`;
                duePlants.forEach(p => {
                    response += `• ${p.name} (Due: ${dayLabel(new Date(p.next_watering_at))})\n`;
                });
            }

            if (hasAppliances) {
                response += `\n🛠️ **Appliance Servicing:**\n`;
                dueAppliances.forEach(a => {
                    response += `• ${a.name} service (Due: ${dayLabel(new Date(a.next_service_date))})\n`;
                });
            }

            if (hasSubtasks) {
                response += `\n📋 **Checklists & Sub-tasks:**\n`;
                dueSubtasks.forEach(s => {
                    response += `• [${s.type}] ${s.item} - ${s.task} (Due: ${dayLabel(new Date(s.date))})\n`;
                });
            }

            return response;
        }

        // 2. PAINT & WALLS
        if (contains(["paint", "color", "colour", "shade", "wall", "coat"])) {
            // Check for specific room
            const matches = paintCodes.filter(p =>
                matchesWordOrPlural(p.room, q) ||
                (p.code && q.includes(p.code.toLowerCase()))
            );

            if (matches.length > 0) {
                if (matches.length === 1) {
                    const p = matches[0];
                    return `Your ${p.room} is painted with ${p.brand} ${p.code} (${p.finish}).`;
                }
                return `I found ${matches.length} paint records:\n` +
                    matches.map(p => `• ${p.room}: ${p.brand} ${p.code}`).join("\n");
            }

            if (paintCodes.length > 0) {
                return "I have paint codes for: " + paintCodes.map(p => p.room).join(", ") + ". Which one would you like to know about?";
            }
            return "No paint records found. You can add them in Your Home!";
        }

        // 3. SERVICE PROVIDERS & CONTACTS
        if (contains(["provider", "plumber", "electrician", "handyman", "who", "contact", "phone", "call", "emergency", "service", "sparky", "gardener", "pool"])) {
            // Check for category matches
            const categoryMatch = providers.filter(p => contains([p.category, p.name]));

            if (categoryMatch.length > 0) {
                return `Here are the contacts I found:\n` +
                    categoryMatch.map(p => `• ${p.name} (${p.category}): ${p.phone || p.email || "No contact info saved"}`).join("\n");
            }

            // General request for providers
            if (contains(["list", "show", "all", "what"]) && contains(["provider", "contact", "contractor"])) {
                if (providers.length > 0) {
                    return `Your saved service providers:\n` +
                        providers.slice(0, 5).map(p => `• ${p.name} (${p.category})`).join("\n") +
                        (providers.length > 5 ? `\n...and ${providers.length - 5} more.` : "");
                }
                return "You haven't added any service providers yet.";
            }
        }

        // 4. HOME LIBRARY DOCUMENTS
        // Use more specific 'plan' keyword that doesn't trigger 'plant'
        if (contains(["document", "file", "pdf", "library", "deed", "coc", "plan", "drawing", "paper", "certificate", "insurance", "policy"])) {
            const matches = vaultItems.filter(v => {
                return q.includes(v.label.toLowerCase()) || q.includes(v.category.toLowerCase()) || matchesWordOrPlural(v.label, q);
            });

            if (matches.length > 0) {
                return `I found these documents in Your Home:\n` +
                    matches.map(v => `• ${v.label} (${v.category})`).join("\n");
            }

            if (contains(["list", "show", "all"]) && vaultItems.length > 0) {
                return `Your Home Records contain:\n` +
                    vaultItems.map(v => `• ${v.label}`).join("\n");
            }
            // If the query was purely about 'plans' and no match, clarify
            if (matchesWordOrPlural("plan", q)) {
                return "I couldn't find any documents related to 'plans' in Your Home. Check the Home Records section!";
            }
        }

        // 5. APPLIANCES & SERVICE HISTORY
        if (contains(["appliance", "service", "maintenance", "history", "due", "fix", "repair", "broken", "last", "next", "machine", "fridge", "refrigerator", "stove", "oven", "geyser", "boiler", "aircon", "ac", "dishwasher", "laundry", "wash"])) {
            // Specific appliance lookup
            const foundApp = namedAppliance || appliances.find(a => contains([a.name, a.brand, a.model_number]) || (a.category && contains([a.category])));

            if (foundApp) {
                const logs = serviceLogs.filter(l => l.appliance_id === foundApp.id);

                if (contains(["when", "last", "history"])) {
                    if (logs.length > 0) {
                        return `The ${foundApp.name} was last serviced on ${logs[0].date}. ${logs[0].description ? "Note: " + logs[0].description : ""}`;
                    }
                    if (foundApp.last_service_date) return `The ${foundApp.name} was last serviced on ${foundApp.last_service_date}.`;
                    return `I don't have a service history for the ${foundApp.name}.`;
                }

                if (contains(["next", "due", "soon"])) {
                    if (foundApp.next_service_date) return `The ${foundApp.name} is due for service on ${foundApp.next_service_date}.`;
                    return `No upcoming service scheduled for the ${foundApp.name}.`;
                }

                if (contains(["install", "buy", "bought", "purchased", "warranty"])) {
                    let resp = `Details for ${foundApp.brand || ''} ${foundApp.name}:\n`;
                    if (foundApp.installation_date) resp += `• Installed: ${foundApp.installation_date}\n`;
                    if (foundApp.warranty_expiry) resp += `• Warranty: Expires ${foundApp.warranty_expiry}\n`;
                    if (foundApp.model_number) resp += `• Model: ${foundApp.model_number}`;
                    return resp;
                }
            }

            // General maintenance summary
            if (contains(["list", "all", "what", "next", "upcoming", "soon"])) {
                const upcoming = appliances
                    .filter(a => a.next_service_date)
                    .sort((a, b) => new Date(a.next_service_date) - new Date(b.next_service_date))
                    .slice(0, 3);

                if (upcoming.length > 0) {
                    return `Upcoming maintenance:\n` +
                        upcoming.map(a => `• ${a.name}: Due ${a.next_service_date}`).join("\n");
                }
                return "All your appliances look up-to-date! No immediate services needed.";
            }

            if (contains(["list", "all", "appliance", "item"])) {
                if (appliances.length > 0) {
                    return `You have ${appliances.length} appliances:\n` +
                        appliances.map(a => `• ${a.brand || ''} ${a.name}`).join("\n");
                }
            }
        }

        // 6. FURNITURE & LOCATIONS
        if (contains(["furniture", "couch", "bed", "table", "chair", "sofa", "desk", "room", "where", "location", "kitchen", "lounge", "living", "bedroom", "bathroom", "garage", "patio"])) {
            // Fix: furniture's real column is `name`, not `item_name` (see
            // fix in supabase/functions/send-service-emails/index.ts for the
            // matching server-side bug) — useFurniture()'s select("*") never
            // returns item_name, so these previously always rendered
            // "undefined is in the undefined".
            const matches = furniture.filter(f => contains([f.name, f.room, f.notes]));

            if (matches.length > 0) {
                if (matches.length === 1) {
                    const f = matches[0];
                    return `The ${f.name} is in the ${f.room}. ${f.notes ? "Note: " + f.notes : ""}`;
                }
                return `I found these items:\n` +
                    matches.map(f => `• ${f.name} (${f.room})`).join("\n");
            }

            if (contains(["all", "list", "show"]) && furniture.length > 0) {
                return `Furniture list:\n` +
                    furniture.map(f => `• ${f.name} in the ${f.room}`).join("\n");
            }
        }

        // 7. UTILITIES & WIFI
        if (contains(["wifi", "password", "internet", "code", "connect"])) {
            const wifi = quickInfo.find(i => i.info_type === 'wifi' || i.label?.toLowerCase().includes('wifi'));
            if (wifi) return `WiFi Hookup:\nNetwork: ${wifi.value}\nPassword: ${wifi.sub_value}`;
        }

        if (contains(["water", "valve", "shutoff", "leak", "off", "utility"])) {
            const water = quickInfo.find(i => i.info_type === 'water_shutoff');
            if (water) return `Safe to know: The water shut-off valve is located: ${water.sub_value || water.value}`;
        }

        if (contains(["power", "electric", "db", "board", "trip", "switch", "fuse", "panel", "box"])) {
            const dbBoard = quickInfo.find(i => i.info_type === 'db_board');
            if (dbBoard) return `The DB Board (electrical panel) is located: ${dbBoard.sub_value || dbBoard.value}`;
        }

        // 8. PLANTS & CARE
        if (contains(["plant", "leaf", "tree", "flower", "water", "thirsty", "dry", "care", "sun", "light"])) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (contains(["thirsty", "due", "which", "need"])) {
                const due = plants.filter(p => !p.is_paused && new Date(p.next_watering_at) <= today);
                if (due.length > 0) return `These plants need water right now:\n` + due.map(p => `• ${p.name}`).join("\n");
                return "All your plants are currently happy and watered!";
            }

            const foundPlant = plants.find(p => contains([p.name]));
            if (foundPlant) {
                if (contains(["sun", "light"])) return `The ${foundPlant.name} likes ${foundPlant.sunlight_preference} light.`;
                return `Care for ${foundPlant.name}:\n• Water: Every ${foundPlant.watering_frequency} ${foundPlant.watering_unit}\n• Light: ${foundPlant.sunlight_preference}\n• Note: ${foundPlant.notes || 'None'}`;
            }

            if (plants.length > 0) {
                return `I have care info for ${plants.length} plants:\n` + plants.map(p => `• ${p.name}`).join("\n");
            }
        }

        // 9. FALLBACK
        return "I'm not quite sure how to answer that yet. Try asking about your paint colors, appliances, service contacts, or which plants need watering!";
    };

    const handleSend = async () => {
        if (!input.trim()) return;

        const userMsg = { role: "user", content: input };
        setMessages(prev => [...prev, userMsg]);
        setInput("");
        setIsTyping(true);

        // Simulate "thinking" delay for realism, even though it's instant
        setTimeout(() => {
            // Fix: searchHome() ran unguarded — any bad match/regex or
            // unexpected data shape (e.g. a null field) would throw inside
            // this timeout and silently leave the assistant stuck "typing"
            // forever with no visible error and no reply.
            let answer;
            try {
                answer = searchHome(userMsg.content);
            } catch (err) {
                console.error("AskHomeEzi search error:", err);
                answer = "Sorry, I couldn't quite process that question. Try rephrasing it, or ask about paint colors, appliances, service contacts, or which plants need watering.";
            }
            setMessages(prev => [...prev, { role: "assistant", content: answer }]);
            setIsTyping(false);
        }, 600);
    };

    // ... inside AskHomeEzi ...
    const location = useLocation();
    const shouldHide =
        location.pathname === "/app" ||
        location.pathname === "/profile" ||
        location.pathname === "/directory" ||
        location.pathname === "/auth" ||
        location.pathname === "/claim-property" ||
        location.pathname === "/setup" ||
        location.pathname === "/home-records";

    if (mode === "embedded") {
        return (
            <div className="flex flex-col w-full">
                <div className={cn("w-full h-[400px] flex flex-col rounded-[2rem] overflow-hidden border border-white/20 bg-white/40 backdrop-blur-md shadow-sm", className)}>
                    {/* Header */}
                    <div className="bg-white/40 p-4 flex items-center gap-3 border-b border-white/10">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-primary to-purple-500 flex items-center justify-center shadow-lg">
                            <Bot className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h3 className="font-bold text-foreground text-sm">Ask HomeEzi</h3>
                            <p className="text-xs text-muted-foreground">Home Assistant</p>
                        </div>
                    </div>

                    {/* Messages Area */}
                    <div
                        ref={messagesContainerRef}
                        className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide bg-white/20"
                    >
                        {messages.map((msg, idx) => (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                key={idx}
                                className={cn(
                                    "max-w-[85%] rounded-2xl p-3 text-xs md:text-sm leading-relaxed",
                                    msg.role === "user"
                                        ? "bg-primary text-white ml-auto rounded-tr-sm"
                                        : "bg-white shadow-sm border border-white/50 mr-auto rounded-tl-sm text-foreground"
                                )}
                            >
                                <div className="whitespace-pre-line">
                                    {msg.content}
                                </div>
                            </motion.div>
                        ))}
                        {isTyping && (
                            <div className="bg-white/40 w-fit p-3 rounded-2xl rounded-tl-sm flex gap-1 items-center">
                                <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                                <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                                <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div className="p-4 bg-white/40 border-t border-white/10 space-y-3">
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSend();
                            }}
                            className="relative flex items-center gap-2"
                        >
                            <Input
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onFocus={(e) => {
                                    if (onInputFocus) onInputFocus(true);
                                    setTimeout(() => {
                                        e.target.scrollIntoView({ behavior: "smooth", block: "center" });
                                    }, 350);
                                }}
                                onBlur={() => onInputFocus && onInputFocus(false)}
                                placeholder="Type a question..."
                                className="rounded-full bg-white/80 border-none shadow-sm h-10 pr-12 text-base md:text-sm focus-visible:ring-primary/20"
                            />
                            <Button
                                type="submit"
                                size="icon"
                                disabled={!input.trim()}
                                className="absolute right-1 w-8 h-8 rounded-full shadow-md bg-primary hover:bg-primary/90 flex items-center justify-center active:scale-90 transition-transform"
                            >
                                <Send className="w-4 h-4 text-white ml-0.5" />
                            </Button>
                        </form>
                    </div>
                </div>
                {/* Spacer to push the assistant above the keyboard on mobile devices */}
                {isKeyboardOpen && (
                    <div style={{ height: `${keyboardHeight}px` }} className="w-full transition-all duration-300" />
                )}
            </div>
        );
    }

    // Floating Mode (Original)
    return (
        <>
            {/* FAB Trigger - Positioned next to the Main FAB */}
            {!isOpen && !shouldHide && mode === "floating" && (
                <motion.button
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsOpen(true)}
                    className="fixed bottom-24 right-24 z-40 w-14 h-14 rounded-full bg-gradient-to-tr from-primary to-primary/80 text-white shadow-lg shadow-primary/25 flex items-center justify-center"
                >
                    <Sparkles className="w-6 h-6 fill-white/20" />
                </motion.button>
            )}

            {/* Chat Window */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        className={cn(
                            "fixed right-5 z-50 w-[90vw] max-w-[380px] flex flex-col shadow-2xl rounded-[2rem] overflow-hidden border border-white/20 bg-white/80 backdrop-blur-3xl transition-all duration-300",
                            isKeyboardOpen ? "" : "bottom-24 h-[500px]"
                        )}
                        style={isKeyboardOpen ? {
                            bottom: `${keyboardHeight + 10}px`,
                            maxHeight: `calc(100vh - ${keyboardHeight}px - 30px)`,
                            height: "320px"
                        } : undefined}
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-4 flex items-center justify-between border-b border-white/10">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-primary to-purple-500 flex items-center justify-center md:shadow-lg">
                                    <Bot className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-foreground text-sm">Ask HomeEzi</h3>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 rounded-full hover:bg-black/5"
                                onClick={() => setIsOpen(false)}
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        </div>

                        {/* Messages Area */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide">
                            {messages.map((msg, idx) => (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    key={idx}
                                    className={cn(
                                        "max-w-[85%] rounded-2xl p-3 text-sm leading-relaxed",
                                        msg.role === "user"
                                            ? "bg-primary text-white ml-auto rounded-tr-sm"
                                            : "bg-white/60 shadow-sm border border-white/40 mr-auto rounded-tl-sm text-foreground"
                                    )}
                                >
                                    <div className="whitespace-pre-line">
                                        {msg.content}
                                    </div>
                                </motion.div>
                            ))}
                            {isTyping && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="bg-white/40 w-fit p-3 rounded-2xl rounded-tl-sm flex gap-1 items-center"
                                >
                                    <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                                    <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                                    <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                                </motion.div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input Area */}
                        <div className="p-4 bg-white/40 border-t border-white/20 space-y-3">
                            {/* Suggestions pills */}
                            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide mask-fade-right">
                                {AI_SUGGESTIONS.map((s, i) => (
                                    <button
                                        key={i}
                                        onClick={() => {
                                            setInput(s);
                                            // Optional: Auto-send on click?
                                        }}
                                        className="shrink-0 text-[10px] font-bold bg-white/60 hover:bg-white border border-white/40 px-3 py-1.5 rounded-full text-muted-foreground transition-colors whitespace-nowrap shadow-sm"
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>

                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSend();
                                }}
                                className="relative flex items-center gap-2"
                            >
                                <Input
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onFocus={(e) => {
                                        setTimeout(() => {
                                            e.target.scrollIntoView({ behavior: "smooth", block: "center" });
                                        }, 350);
                                    }}
                                    placeholder="Ask anything..."
                                    className="rounded-full bg-white border-none shadow-sm h-12 pr-12 text-base md:text-sm focus-visible:ring-primary/20"
                                />
                                <Button
                                    type="submit"
                                    size="icon"
                                    disabled={!input.trim()}
                                    className="absolute right-1 w-10 h-10 rounded-full shadow-md bg-primary hover:bg-primary/90 z-10 flex items-center justify-center active:scale-90 transition-transform"
                                >
                                    <Send className="w-4 h-4 text-white ml-0.5" />
                                </Button>
                            </form>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default AskHomeEzi;
